import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import DateRangePicker from "@/components/DateRangePicker";
import SegmentBadge from "@/components/SegmentBadge";
import { useProductGroups, useSegmentation } from "@/lib/hooks";
import { presets, priorYearRange, type Range } from "@/lib/dates";
import { fmtDelta, fmtInt, fmtUSDCompact, fmtDate, deltaTone } from "@/lib/format";
import { useSort } from "@/lib/useSort";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";
import type { Segment, SegmentedCustomer } from "@/lib/api";

const ACCESSORS = {
  name: (c: SegmentedCustomer) => c.customer_name.toLowerCase(),
  monitor_count: (c: SegmentedCustomer) => c.monitor_count,
  current_qty: (c: SegmentedCustomer) => c.current_qty,
  prior_qty: (c: SegmentedCustomer) => c.prior_qty,
  yoy_delta_qty: (c: SegmentedCustomer) => c.yoy_delta_qty,
  yoy_delta_pct: (c: SegmentedCustomer) => c.yoy_delta_pct,
  current_revenue: (c: SegmentedCustomer) => c.current_revenue,
  last_order: (c: SegmentedCustomer) => c.last_order_date,
};

const SECTION_ORDER: Segment[] = ["lost", "declining", "stable", "growing", "new"];
const SECTION_LABELS: Record<Segment, string> = {
  lost: "Lost — no orders in current window",
  declining: "Declining — down >25% YoY",
  stable: "Stable — within ±25%",
  growing: "Growing — up >25% YoY",
  new: "New — no prior baseline",
};

const defaultPreset = presets[0];

export default function Segmentation() {
  const [searchParams] = useSearchParams();
  const atRiskFocus = searchParams.get("focus") === "at-risk";

  const [range, setRange] = useState<Range>(defaultPreset.range());
  const [presetId, setPresetId] = useState<string | null>(defaultPreset.id);
  // At-risk drill-down from the Overview uses all products to match the KPI.
  const [productGroupKey, setProductGroupKey] = useState<string>(
    atRiskFocus ? "all" : "catheters",
  );

  const { data: pgData } = useProductGroups();

  const productGroupsForQuery = useMemo<string[]>(() => {
    if (productGroupKey === "all") return [];
    if (productGroupKey === "catheters") return ["UOT", "IAP", "SmartFoley Adapter"];
    return [productGroupKey];
  }, [productGroupKey]);

  const compare = priorYearRange(range);
  const { data, isLoading, error } = useSegmentation(
    range.from,
    range.to,
    compare.from,
    compare.to,
    productGroupsForQuery,
  );

  const grouped = useMemo(() => {
    const map: Record<Segment, SegmentedCustomer[]> = {
      lost: [],
      declining: [],
      stable: [],
      growing: [],
      new: [],
    };
    data?.customers.forEach((c) => map[c.segment].push(c));
    return map;
  }, [data]);

  function exportCsv() {
    downloadXlsx(
      stampedName("customers-segmentation"),
      [
        "Customer", "Segment", "Monitors", "Current qty", "Current revenue", "Prior qty",
        "Prior revenue", "YoY change (qty)", "YoY %", "Last order",
      ],
      (data?.customers ?? []).map((c) => [
        c.customer_name, c.segment, c.monitor_count ?? 0, c.current_qty, c.current_revenue,
        c.prior_qty, c.prior_revenue, c.yoy_delta_qty, c.yoy_delta_pct ?? "", c.last_order_date,
      ]),
    );
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {atRiskFocus ? "At-risk customers" : "Customers"}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            {atRiskFocus
              ? "Lost and declining (down >25% YoY) accounts — the at-risk count from the Overview."
              : "Year-over-year segmentation, with like-for-like prior period."}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <DateRangePicker
            value={range}
            presetId={presetId}
            onChange={(r, id) => {
              setRange(r);
              setPresetId(id);
            }}
          />
          <div className="flex items-center gap-2">
            <ProductGroupSelector
              value={productGroupKey}
              onChange={setProductGroupKey}
              options={pgData?.product_groups ?? []}
            />
            <DownloadButton onClick={exportCsv} disabled={!data?.customers.length} />
          </div>
        </div>
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {data && (
        <p className="mb-4 text-xs text-ink-500">
          Current: {data.period.from} → {data.period.to}. Compared to {data.compare_period.from} → {data.compare_period.to}.{" "}
          {data.customers.length} customers with activity in either window.
        </p>
      )}

      <div className="space-y-6">
        {SECTION_ORDER.map((segment) => {
          const rows = grouped[segment];
          return (
            <SegmentSection
              key={segment}
              segment={segment}
              rows={rows}
              loading={isLoading}
              defaultExpanded={atRiskFocus ? segment === "lost" || segment === "declining" : true}
            />
          );
        })}
      </div>
    </div>
  );
}

function ProductGroupSelector({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { product_group: string; parent_group: string | null }[];
}) {
  const topLevel = options.filter((o) => o.parent_group == null);
  return (
    <label className="flex items-center gap-2 text-xs text-ink-500">
      Product
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-ink-200 px-2 py-1 text-xs text-ink-900"
      >
        <option value="catheters">Catheters (UOT + IAP + SmartFoley)</option>
        <option value="all">All products</option>
        {topLevel.map((o) => (
          <option key={o.product_group} value={o.product_group}>
            {o.product_group}
          </option>
        ))}
      </select>
    </label>
  );
}

function SortTh({
  children,
  onClick,
  align,
}: {
  children: React.ReactNode;
  onClick: () => void;
  align: "left" | "right";
}) {
  return (
    <th className={`px-5 py-2 ${align === "left" ? "text-left" : "text-right"}`}>
      <button
        type="button"
        onClick={onClick}
        className="font-medium uppercase tracking-wide text-ink-500 hover:text-ink-900"
      >
        {children}
      </button>
    </th>
  );
}

function SegmentSection({
  segment,
  rows,
  loading,
  defaultExpanded = true,
}: {
  segment: Segment;
  rows: SegmentedCustomer[];
  loading: boolean;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { sorted, toggle, arrow } = useSort<SegmentedCustomer>(rows, ACCESSORS, "current_qty", "desc");
  return (
    <section className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between border-b border-ink-200 px-5 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <SegmentBadge segment={segment} />
          <span className="text-sm font-medium text-ink-900">
            {SECTION_LABELS[segment]}
          </span>
          <span className="text-xs text-ink-500">{rows.length}</span>
        </div>
        <span className="text-xs text-ink-500">{expanded ? "Hide" : "Show"}</span>
      </button>
      {expanded && (
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-ink-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-500">No customers in this tier.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <SortTh align="left" onClick={() => toggle("name", "asc")}>Customer{arrow("name")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("monitor_count")}>Monitors{arrow("monitor_count")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("current_qty")}>Current qty{arrow("current_qty")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("prior_qty")}>Prior qty{arrow("prior_qty")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("yoy_delta_qty")}>YoY Δ{arrow("yoy_delta_qty")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("yoy_delta_pct")}>YoY %{arrow("yoy_delta_pct")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("current_revenue")}>Current revenue{arrow("current_revenue")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("last_order", "asc")}>Last order{arrow("last_order")}</SortTh>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {sorted.map((r) => {
                  const tone = deltaTone(r.yoy_delta_pct);
                  return (
                    <tr key={r.customer_id} className="hover:bg-ink-50">
                      <td className="px-5 py-2 font-medium text-ink-900">
                        <Link to={`/customers/${encodeURIComponent(r.customer_id)}`} className="hover:text-accent-600">
                          {r.customer_name}
                        </Link>
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums text-ink-700">
                        {r.monitor_count || "—"}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">{fmtInt(r.current_qty)}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-ink-500">{fmtInt(r.prior_qty)}</td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        <span className={tone === "up" ? "text-success" : tone === "down" ? "text-danger" : ""}>
                          {r.yoy_delta_qty > 0 ? "+" : ""}{fmtInt(r.yoy_delta_qty)}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        <span className={tone === "up" ? "text-success" : tone === "down" ? "text-danger" : "text-ink-500"}>
                          {fmtDelta(r.yoy_delta_pct)}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums text-ink-700">{fmtUSDCompact(r.current_revenue)}</td>
                      <td className="px-5 py-2 text-right text-ink-500">{fmtDate(r.last_order_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
